/**
 * Cartões de dados em grade bento.
 *
 * Substitui os quatro "indicadores" que cada tela tinha desenhado do seu jeito
 * (Vendas, Carteira, Planejamento, os cartões-filtro de Inadimplentes e
 * Controle). Um vocabulário só:
 *
 *  · GRADE de 4 colunas no desktop e 2 no celular, com `dense`: um cartão
 *    "destaque" ocupa 2×2 e os normais encaixam ao lado dele — o herói da tela
 *    fica onde o olho começa, os demais ao redor.
 *  · CARTÃO na ordem de leitura métrica → variação → contexto: ícone e rótulo,
 *    o número, a pílula de variação, a nota e, no rodapé, a tendência.
 *  · Número FLUIDO: o tamanho acompanha a largura do próprio cartão (container
 *    query), não a da tela, e encolhe com a quantidade de caracteres — "R$ 1,5
 *    mi" e "16" cabem igual num cartão de 160px ou de 400px, sem quebrar.
 *  · Cor com parcimônia: o tom tinge o chip do ícone e a tendência; o número
 *    só ganha cor quando o tom é um alerta. O destaque leva um brilho da marca.
 *
 * Cartão clicável (filtro, atalho) levanta levemente no hover e usa o mesmo
 * `selecionado` laranja do resto do app quando está ativo.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { cx } from '../../lib/cx.ts';
import { Sparkline } from '../charts/index.tsx';

export type TomDado = 'neutro' | 'marca' | 'sucesso' | 'alerta' | 'perigo';
export type TamanhoDado = 'normal' | 'largo' | 'destaque' | 'total';

const COR: Record<TomDado, { forte: string; fundo: string }> = {
  neutro: { forte: 'var(--muted)', fundo: 'var(--gray-100)' },
  marca: { forte: 'var(--brand)', fundo: 'var(--brand-subtle)' },
  sucesso: { forte: 'var(--success)', fundo: 'var(--success-subtle)' },
  alerta: { forte: 'var(--warn)', fundo: 'var(--warn-subtle)' },
  perigo: { forte: 'var(--danger)', fundo: 'var(--danger-subtle)' },
};

const SPAN: Record<TamanhoDado, string> = {
  normal: 'col-span-1',
  largo: 'col-span-2',
  destaque: 'col-span-2 lg:row-span-2',
  total: 'col-span-2 lg:col-span-4',
};

export function GradeBento({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('grid grid-flow-row-dense grid-cols-2 gap-3 lg:grid-cols-4', className)}>
      {children}
    </div>
  );
}

export interface Variacao {
  /** "▲ 12%", "▼ 3", "= igual". */
  texto: string;
  /** Contexto da comparação: "vs ontem", "out vs set". */
  detalhe?: string;
  /** Verde quando é boa notícia, vermelho quando ruim; nulo fica neutro. */
  bom: boolean | null;
}

/**
 * Tamanho do número: cresce com a largura do cartão (cqi) e encolhe com o
 * comprimento do texto, entre um piso e um teto em px. A fonte mono tem ~0,6em
 * por caractere; 120/n cqi deixa folga para o padding.
 */
function tamanhoNumero(valor: string, destaque: boolean): string {
  const n = Math.max(valor.length, 3);
  const [piso, teto, limite] = destaque ? [30, 56, 15] : [20, 34, 18];
  return `clamp(${piso}px, ${Math.min(limite, 120 / n).toFixed(2)}cqi, ${teto}px)`;
}

export function CartaoDado({
  rotulo,
  valor,
  Icone,
  tom = 'neutro',
  nota,
  variacao,
  tendencia,
  tamanho = 'normal',
  ativo,
  onClick,
  para,
  children,
  className,
}: {
  rotulo: string;
  valor: string;
  Icone?: LucideIcon;
  tom?: TomDado;
  nota?: ReactNode;
  variacao?: Variacao | null;
  /** Série para a área de tendência no rodapé (12 meses, por exemplo). */
  tendencia?: number[];
  tamanho?: TamanhoDado;
  /** Cartão-filtro escolhido. */
  ativo?: boolean;
  onClick?: () => void;
  /** Cartão-atalho para outra tela. */
  para?: string;
  /** Conteúdo extra entre o número e a tendência (barra de meta, faixas...). */
  children?: ReactNode;
  className?: string;
}) {
  const destaque = tamanho === 'destaque';
  const cor = COR[tom];
  const interativo = Boolean(onClick || para);
  const corValor = tom === 'perigo' || tom === 'alerta' ? cor.forte : 'var(--fg)';

  const corpo = (
    <>
      {/* Brilho da marca no canto do destaque — o único cartão que o leva. */}
      {destaque && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_70%_at_100%_0%,var(--brand-subtle),transparent_65%)]"
        />
      )}

      <div className="relative flex items-start gap-2.5">
        {Icone && (
          <span
            aria-hidden
            className="grid size-8 shrink-0 place-items-center rounded-[10px]"
            style={{ background: cor.fundo, color: cor.forte }}
          >
            <Icone size={16} strokeWidth={2.2} />
          </span>
        )}
        {/* Até duas linhas: "Instalado sem produto" cortado em "Instalado
            sem…" num cartão de meia tela não diz o que o número é. */}
        <p className="line-clamp-2 min-h-8 min-w-0 flex-1 self-center text-[13px] leading-tight font-medium text-[var(--muted)]">
          {rotulo}
        </p>
        {para && <ChevronRight size={16} className="mt-2 shrink-0 text-[var(--faint)]" />}
      </div>

      <p
        className={cx('num relative font-semibold tracking-tight', destaque ? 'mt-5' : 'mt-3')}
        style={{ fontSize: tamanhoNumero(valor, destaque), lineHeight: 1.05, color: corValor }}
      >
        {valor}
      </p>

      {(variacao || nota) && (
        <div className="relative mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          {variacao && (
            <span
              className="num inline-flex items-center rounded-[var(--r-full)] px-2 py-0.5 text-xs font-medium"
              style={
                variacao.bom === null
                  ? { background: 'var(--gray-100)', color: 'var(--muted)' }
                  : {
                      background: variacao.bom ? 'var(--success-subtle)' : 'var(--danger-subtle)',
                      color: variacao.bom ? 'var(--success)' : 'var(--danger)',
                    }
              }
            >
              {variacao.texto}
            </span>
          )}
          {variacao?.detalhe && (
            <span className="text-xs text-[var(--faint)]">{variacao.detalhe}</span>
          )}
          {nota && <span className="w-full text-xs leading-snug text-[var(--faint)]">{nota}</span>}
        </div>
      )}

      {/* No destaque o conteúdo extra desce para o rodapé: ocupa o 2×2 em vez
          de deixar meio cartão vazio sob o número. */}
      {children && (
        <div className={cx('relative', destaque ? 'mt-auto pt-5' : 'mt-3')}>{children}</div>
      )}

      {tendencia && tendencia.length > 1 && (
        // Sangra até as bordas do cartão: a área lê como fundo, não como
        // gráfico a decifrar.
        <div className="relative -mx-4 -mb-4 mt-auto pt-3 sm:-mx-5 sm:-mb-5">
          <Sparkline valores={tendencia} cor={cor.forte} area altura={destaque ? 72 : 40} />
        </div>
      )}
    </>
  );

  const classes = cx(
    '@container relative flex min-w-0 flex-col overflow-hidden rounded-[20px] border border-[var(--border)] bg-[var(--surface)] p-4 text-left sm:p-5',
    SPAN[tamanho],
    interativo &&
      'anel-foco cursor-pointer transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-[var(--border-strong)] hover:shadow-[0_12px_32px_-16px_rgb(0_0_0/0.28)] active:translate-y-0',
    ativo && 'selecionado',
    className,
  );

  if (para) {
    return (
      <Link to={para} className={classes}>
        {corpo}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} aria-pressed={ativo} className={classes}>
        {corpo}
      </button>
    );
  }
  return <div className={classes}>{corpo}</div>;
}

/**
 * Números de apoio dentro de um cartão — o rodapé do destaque. Rótulo em
 * cima, número embaixo, em colunas que quebram no celular.
 */
export function MiniDados({
  itens,
}: {
  itens: { rotulo: string; valor: string; tom?: TomDado }[];
}) {
  return (
    <dl className="grid grid-cols-2 gap-3 border-t border-[var(--border)] pt-4 sm:grid-cols-3">
      {itens.map((i) => (
        <div key={i.rotulo} className="min-w-0">
          <dt className="truncate text-xs text-[var(--muted)]">{i.rotulo}</dt>
          <dd
            className="num mt-0.5 truncate text-lg font-semibold"
            style={{ color: i.tom && i.tom !== 'neutro' ? COR[i.tom].forte : 'var(--fg)' }}
          >
            {i.valor}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Barra empilhada com legenda — composição de um total (faixas de atraso,
 * tempo em fila). Cada fatia leva o próprio valor na legenda: a cor nunca é
 * a única pista.
 */
export function BarraComposicao({
  fatias,
}: {
  fatias: { rotulo: string; valor: number; cor: string; texto?: string }[];
}) {
  const total = fatias.reduce((s, f) => s + f.valor, 0);
  if (total <= 0) return null;
  const visiveis = fatias.filter((f) => f.valor > 0);
  return (
    <div>
      <div className="flex h-3 gap-0.5 overflow-hidden rounded-[var(--r-full)]">
        {visiveis.map((f) => (
          <span
            key={f.rotulo}
            title={`${f.rotulo}: ${f.texto ?? f.valor}`}
            style={{ width: `${(f.valor / total) * 100}%`, background: f.cor }}
          />
        ))}
      </div>
      <ul className="m-0 mt-2.5 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-xs text-[var(--muted)]">
        {visiveis.map((f) => (
          <li key={f.rotulo} className="flex items-center gap-1.5">
            <span aria-hidden className="size-2 rounded-sm" style={{ background: f.cor }} />
            {f.rotulo}
            <span className="num text-[var(--fg)]">{f.texto ?? f.valor}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
